import { createTheme } from '@mui/material/styles';

// Extend the theme to include custom properties
declare module '@mui/material/styles' {
  interface Theme {
    customPalette: {
      severity: {
        critical: string;
        high: string;
        medium: string;
        low: string;
        info: string;
      };
      status: {
        active: string;
        inactive: string;
      };
      surface: {
        card: string;
        drawer: string;
        appBar: string;
      };
      border: {
        subtle: string;
      };
      glow: {
        primary: string;
        hover: string;
      };
    };
  }
  interface ThemeOptions {
    customPalette?: {
      severity?: {
        critical?: string;
        high?: string;
        medium?: string;
        low?: string;
        info?: string;
      };
      status?: {
        active?: string;
        inactive?: string;
      };
      surface?: {
        card?: string;
        drawer?: string;
        appBar?: string;
      };
      border?: {
        subtle?: string;
      };
      glow?: {
        primary?: string;
        hover?: string;
      };
    };
  }
}

export const getDesignTokens = (mode: 'light' | 'dark') => ({
  palette: {
    mode,
    primary: {
      main: '#FF4800', // OpenAna Orange
      light: '#FF6B33',
      dark: '#CC3A00',
    },
    secondary: {
      main: '#FF4800', // OpenAna Orange
      light: '#FF6B33',
      dark: '#CC3A00',
    },
    error: {
      main: '#ef4444', // Modern red
      light: '#f87171',
      dark: '#dc2626',
    },
    warning: {
      main: '#f59e0b', // Modern amber
      light: '#fbbf24',
      dark: '#d97706',
    },
    success: {
      main: '#10b981', // Modern emerald
      light: '#34d399',
      dark: '#059669',
    },
    background: {
      default: mode === 'dark' ? '#000000' : '#FFFFFF',
      paper: mode === 'dark' ? '#111111' : '#F8FAFC',
    },
    text: {
      primary: mode === 'dark' ? '#f1f5f9' : '#1e293b',
      secondary: mode === 'dark' ? '#94a3b8' : '#64748b',
    },
  },
  customPalette: {
    severity: {
      critical: '#DC2626',
      high: '#EA580C',
      medium: '#D97706',
      low: '#15803D',
      info: mode === 'dark' ? '#4B5563' : '#6B7280'
    },
    status: {
      active: '#166534',
      inactive: mode === 'dark' ? '#374151' : '#9CA3AF'
    },
    surface: {
      card: mode === 'dark' 
        ? 'rgba(17, 17, 17, 0.6)' 
        : 'rgba(248, 250, 252, 0.8)',
      drawer: mode === 'dark' 
        ? 'rgba(17, 17, 17, 0.95)' 
        : 'rgba(248, 250, 252, 0.95)',
      appBar: mode === 'dark' 
        ? 'rgba(17, 17, 17, 0.7)' 
        : 'rgba(248, 250, 252, 0.8)'
    },
    border: {
      subtle: mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.1)' 
        : 'rgba(0, 0, 0, 0.1)'
    },
    glow: {
      primary: 'rgba(255, 72, 0, 0.4)',
      hover: 'rgba(255, 72, 0, 0.2)'
    }
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      letterSpacing: '-0.02em',
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none' as const,
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: mode === 'dark'
            ? 'linear-gradient(145deg, rgba(17, 17, 17, 0.9) 0%, rgba(0, 0, 0, 0.9) 100%)'
            : 'linear-gradient(145deg, rgba(248, 250, 252, 0.9) 0%, rgba(241, 245, 249, 0.9) 100%)',
          backdropFilter: 'blur(10px)',
          boxShadow: 'none',
          borderBottom: mode === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: mode === 'dark'
            ? 'linear-gradient(145deg, rgba(17, 17, 17, 0.8) 0%, rgba(0, 0, 0, 0.8) 100%)'
            : 'linear-gradient(145deg, rgba(248, 250, 252, 0.8) 0%, rgba(241, 245, 249, 0.8) 100%)',
          backdropFilter: 'blur(10px)',
          border: mode === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.1)',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          padding: '8px 16px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(255, 72, 0, 0.4)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #FF4800 0%, #CC3A00 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #FF6B33 0%, #FF4800 100%)',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: mode === 'dark'
            ? 'linear-gradient(145deg, rgba(17, 17, 17, 0.95) 0%, rgba(0, 0, 0, 0.95) 100%)'
            : 'linear-gradient(145deg, rgba(248, 250, 252, 0.95) 0%, rgba(241, 245, 249, 0.95) 100%)',
          backdropFilter: 'blur(10px)',
          borderRight: mode === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          margin: '4px 8px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            background: mode === 'dark'
              ? 'rgba(255, 72, 0, 0.1)'
              : 'rgba(255, 72, 0, 0.05)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: '#FF4800',
            },
            '&.Mui-focused': {
              boxShadow: '0 0 0 2px rgba(255, 72, 0, 0.2)',
            },
          },
        },
      },
    },
  },
});

// Create the light theme
export const lightTheme = createTheme(getDesignTokens('light'));

// Create the dark theme
export const darkTheme = createTheme(getDesignTokens('dark'));

// Create a function to get the current theme
export const getCurrentTheme = () => {
  const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;
  return savedTheme === 'dark' ? darkTheme : lightTheme;
};

// Export a theme that updates based on localStorage
export const theme = getCurrentTheme();